/**
 * Dashboard Page
 *
 * The landing page. It used to be four stat cards — one of which was the
 * hardcoded string '5+' — above two grids of link buttons that the sidebar
 * already covers. Nothing on it moved during the day.
 *
 * Now it answers "how is today going, and is anything waiting on me". The
 * fetches are deliberately not merged into one endpoint: the money numbers
 * come from Mongo and land fast, while the attention panel is stitched from
 * four unrelated sources. One slow source must not hold up the rest.
 *
 * The visitor count used to sit in the fourth tile, on a per-brand BigQuery
 * query that said nothing at all under All Clinics. Estimated blended CAC took
 * its place: it is the number that decides what to spend next, and it reads
 * from the same P&L the other three tiles do.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import {
  dashboardAPI, queuesAPI, expressSetupAPI, physicianAPI, dailyReportAPI,
} from '../services/api'
import {
  ArrowUpRight, ArrowDownRight, Loader2, RefreshCw, AlertCircle, CheckCircle2,
} from 'lucide-react'
import { useAnimatedNumber } from '../hooks/useAnimatedNumber'
import DailyReportTable from '../components/DailyReportTable'
import VelocityHourlyCard from '../components/velocity/VelocityHourlyCard'

/**
 * The P&L layer speaks minor units throughout — `total_revenue` is cents, as
 * the aggregate endpoint's own logging (`total_revenue / 100`) confirms. Every
 * money value on this page goes through here so that conversion happens once.
 */
const currency = (cents, precise = false) => {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: precise ? 2 : 0,
    maximumFractionDigits: precise ? 2 : 0,
  }).format(cents / 100)
}

const count = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US').format(Math.round(value))
}

/**
 * Percentage change, or null when there is nothing to compare against.
 *
 * A jump from zero is not "+100%" or "+∞" — it is a number with no meaningful
 * denominator, and printing one anyway is how a first sale of the day turns
 * into a headline. Callers render the absence instead.
 */
function percentChange(current, prior) {
  if (prior === null || prior === undefined || prior === 0) return null
  if (current === null || current === undefined) return null
  return ((current - prior) / prior) * 100
}

/**
 * Small-caps label, large tinted number, muted comparison line.
 *
 * `value` is the raw number and `format` turns it into text, rather than the
 * caller passing a finished string — the tile animates the number on its way
 * to the new figure, so it has to hold the number itself. tabular-nums keeps
 * the digits from jittering as they roll.
 */
function MetricTile({ label, value, format, delta, comparison, tone = 'text-foreground', loading, href, invert = false }) {
  const animated = useAnimatedNumber(value ?? 0)
  const hasValue = value !== null && value !== undefined && !Number.isNaN(value)

  const body = (
    <CardContent className="p-5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`mt-2 text-3xl font-semibold tabular-nums ${tone}`}>
        {loading
          ? <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          : hasValue ? format(animated) : '—'}
      </div>
      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
        {/* `invert` flips which direction is good news — a CAC below its
            baseline is green — without touching the arrow or the sign, which
            always describe the number's own movement. */}
        {delta !== null && delta !== undefined && !loading && (
          <span className={`flex items-center gap-0.5 font-medium ${(invert ? delta <= 0 : delta >= 0) ? 'text-emerald-600' : 'text-red-600'}`}>
            {delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(delta).toFixed(0)}%
          </span>
        )}
        <span>{loading ? ' ' : comparison}</span>
      </div>
    </CardContent>
  )

  return href
    ? <Link to={href}><Card className="h-full transition-colors hover:border-primary/50">{body}</Card></Link>
    : <Card className="h-full">{body}</Card>
}

const SEVERITY_STYLES = {
  urgent: 'bg-red-500',
  warn: 'bg-amber-500',
  info: 'bg-blue-500',
}

function AttentionRow({ item }) {
  return (
    <Link
      to={item.href}
      className="flex items-start gap-3 rounded-md px-2 py-2 -mx-2 transition-colors hover:bg-muted"
    >
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_STYLES[item.severity]}`} />
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{item.title}</div>
        <div className="text-xs text-muted-foreground">{item.detail}</div>
      </div>
    </Link>
  )
}

/** Seven empty rows: the shape of the week before its numbers arrive. */
const PLACEHOLDER_WEEK = Array.from({ length: 7 }, (_, i) => ({ key: `placeholder-${i}` }))

export default function DashboardPage() {
  const { user, clinics, currentClinicId } = useAuthStore()
  const draftChanges = useDraftStore(state => state.changes)

  const [overview, setOverview] = useState(null)
  const [overviewError, setOverviewError] = useState(null)
  const [loadingOverview, setLoadingOverview] = useState(true)


  const [week, setWeek] = useState(null)
  const [loadingWeek, setLoadingWeek] = useState(true)
  // Which load a response belongs to, so a slow answer for the previous clinic
  // never lands on the current one.
  const weekRequest = useRef(0)

  const [attention, setAttention] = useState([])
  const [loadingAttention, setLoadingAttention] = useState(true)

  const currentClinic = clinics.find(c => c.id === currentClinicId)

  const loadOverview = useCallback(async () => {
    setLoadingOverview(true)
    setOverviewError(null)
    try {
      const response = await dashboardAPI.getOverview()
      setOverview(response.data.data)
    } catch (error) {
      console.error('[DASHBOARD] overview failed:', error)
      setOverviewError(error.response?.data?.error?.message || error.message)
      setOverview(null)
    } finally {
      setLoadingOverview(false)
    }
  }, [])

  /**
   * The last seven days of the Daily Report.
   *
   * Its own fetch, like the attention panel: it reads every clinic
   * collection for the window and is the slowest thing on the page, so it must
   * not hold up the tiles above it.
   *
   * Single-clinic only. /api/daily-report resolves one clinic from the request
   * context and has no aggregate form, and summing brands here would be worse
   * than not showing it — AOV and CAC are ratios that do not add.
   */
  //
  // Loaded in two passes, both started at once. The 'core' pass skips the
  // BigQuery click lookup — the slow part — and returns every figure except the
  // per-channel attribution splits, which it names in `pending_keys`; the full
  // pass fills those in. Until the first answer the table is drawn empty at its
  // final size (DailyReportTable `loading`), so the page does not jump.
  const loadWeek = useCallback(async () => {
    const request = ++weekRequest.current
    const current = () => request === weekRequest.current
    if (!currentClinicId) {
      setWeek(null)
      setLoadingWeek(false)
      return
    }
    setWeek(null)
    setLoadingWeek(true)
    const params = { range: 'last7', granularity: 'daily' }
    let full = false
    const core = dailyReportAPI.get({ ...params, phase: 'core' })
      .then(response => { if (current() && !full) { setWeek(response.data.data); setLoadingWeek(false) } })
      .catch(error => console.log('[DASHBOARD] daily report (first pass) unavailable:', error.message))
    try {
      const response = await dailyReportAPI.get(params)
      full = true
      if (current()) setWeek(response.data.data)
    } catch (error) {
      console.log('[DASHBOARD] daily report unavailable:', error.message)
      // The first pass may still have drawn the table; keep it if so, with its
      // pending columns, rather than blanking a mostly-complete report.
      await core
    } finally {
      if (current()) setLoadingWeek(false)
    }
  }, [currentClinicId])


  /**
   * The attention feed, stitched from four places.
   *
   * allSettled throughout: this panel is a convenience, and one source being
   * down must degrade it rather than empty it.
   */
  const loadAttention = useCallback(async () => {
    setLoadingAttention(true)
    const [setups, physicians, queues] = await Promise.allSettled([
      expressSetupAPI.list(),
      currentClinicId ? physicianAPI.getStats() : Promise.resolve(null),
      currentClinicId ? queuesAPI.getAttention() : Promise.resolve(null),
    ])

    const items = []

    if (setups.status === 'fulfilled') {
      const rows = (setups.value?.data?.data || [])
        .filter(s => s.isExpress === true && !s.isLive && (currentClinicId ? s.clinicId === currentClinicId : true))
      for (const setup of rows) {
        if (setup.waitingOnClinic > 0) {
          items.push({
            id: `setup-clinic-${setup.clinicId}`,
            severity: 'urgent',
            title: `${setup.waitingOnClinic} setup ${setup.waitingOnClinic === 1 ? 'task' : 'tasks'} waiting on you`,
            detail: `${setup.clinicId} · go-live checklist ${setup.progress?.done ?? 0}/${setup.progress?.total ?? 0} done`,
            href: '/admin/express-setup',
          })
        }
        if (setup.waitingOnExpress > 0) {
          items.push({
            id: `setup-express-${setup.clinicId}`,
            severity: 'info',
            title: `${setup.waitingOnExpress} setup ${setup.waitingOnExpress === 1 ? 'task' : 'tasks'} with 5axis`,
            detail: `${setup.clinicId} · nothing needed from you yet`,
            href: '/admin/express-setup',
          })
        }
      }
    }

    if (queues.status === 'fulfilled' && queues.value) {
      const q = queues.value.data.data
      if (q.routing_failed > 0) {
        items.push({
          id: 'routing-failed',
          severity: 'urgent',
          title: `${q.routing_failed} ${q.routing_failed === 1 ? 'order' : 'orders'} failed routing`,
          detail: 'Not moving until someone intervenes',
          href: '/ops/queues',
        })
      }
      if (q.stuck > 0) {
        items.push({
          id: 'stuck-orders',
          severity: 'warn',
          title: `${q.stuck} ${q.stuck === 1 ? 'order' : 'orders'} open over ${q.stuck_after_hours}h`,
          detail: 'Paid but not yet shipped, cancelled or completed',
          href: '/ops/queues',
        })
      }
    }

    if (physicians.status === 'fulfilled' && physicians.value) {
      // The old dashboard read `expiring_licenses` off this response, which the
      // endpoint has never returned — the card showed a blank forever.
      const expiring = physicians.value.data?.data?.licensesSummary?.expiringLicenses || 0
      if (expiring > 0) {
        items.push({
          id: 'licenses',
          severity: 'warn',
          title: `${expiring} ${expiring === 1 ? 'physician has a license' : 'physicians have licenses'} expiring`,
          detail: 'Within the next 90 days',
          href: '/physicians',
        })
      }
    }

    setAttention(items)
    setLoadingAttention(false)
  }, [currentClinicId])

  useEffect(() => { loadOverview() }, [loadOverview, currentClinicId])
  useEffect(() => { loadWeek() }, [loadWeek])
  useEffect(() => { loadAttention() }, [loadAttention])

  // Drafts live in localStorage rather than on the server, so they are read
  // straight off the store instead of being fetched with the rest.
  const allItems = useMemo(() => {
    if (draftChanges.length === 0) return attention
    return [
      ...attention,
      {
        id: 'drafts',
        severity: 'info',
        title: `${draftChanges.length} unpublished ${draftChanges.length === 1 ? 'change' : 'changes'}`,
        detail: 'Staged but not yet shipped in a release',
        href: '/releases',
      },
    ]
  }, [attention, draftChanges.length])

  const today = overview?.today
  const prior = overview?.prior
  // The percentage is against the average of the same slice of the last seven
  // days, over the brands that have such a baseline — `today_comparable` is
  // today over exactly those brands. Yesterday alone swung on which hour two
  // people bought in; see the overview endpoint.
  const baseline = overview?.baseline
  const comparable = overview?.today_comparable
  const canCompare = Boolean(baseline?.sufficient)
  const baselineDelta = (field) => (canCompare ? percentChange(comparable?.[field], baseline?.[field]) : null)
  const thinBaseline = baseline && !baseline.sufficient
    ? `7-day avg ${currency(baseline.revenue)} · too few to compare`
    : 'vs 7-day avg, same time'
  // Blended CAC, against the average of the last whole days. Over plan is bad
  // here, so the tile's own colouring is inverted by negating the change.
  const cac = overview?.cac
  const cacDelta = cac?.spend_reported && cac.today != null && cac.baseline
    ? percentChange(cac.today, cac.baseline)
    : null
  const isAggregate = overview?.scope === 'aggregate'

  const asOf = overview?.as_of
    ? new Date(overview.as_of).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : null

  const refreshAll = () => {
    loadOverview()
    loadWeek()
    loadAttention()
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.firstName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isAggregate
              ? `Today across ${overview?.brands?.length ?? 0} brands`
              : `Today at ${currentClinic?.displayName || overview?.clinic || 'your clinic'}`}
            {asOf && <> · as of {asOf} ET</>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loadingOverview ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {overviewError && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{overviewError}</span>
        </div>
      )}

      {/* Today's numbers. Comparisons are against the average of the same
          slice of the last seven days, and are withheld when that average is
          too thin to mean anything — see the overview endpoint. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricTile
          label="Revenue"
          value={today?.revenue}
          format={currency}
          delta={baselineDelta('revenue')}
          comparison={thinBaseline}
          tone="text-emerald-600"
          loading={loadingOverview}
          href="/reports/pnl"
        />
        <MetricTile
          label="Orders"
          value={today?.orders}
          format={count}
          delta={baselineDelta('orders')}
          comparison={today ? `${count(today.new_orders)} new · ${canCompare ? `${count(baseline.orders)} in the 7-day avg` : 'too few to compare'}` : ''}
          tone="text-blue-600"
          loading={loadingOverview}
          href="/ops/queues"
        />
        <MetricTile
          label="Avg order"
          value={today?.aov}
          format={(v) => currency(v, true)}
          delta={baselineDelta('aov')}
          comparison={thinBaseline}
          tone="text-violet-600"
          loading={loadingOverview}
        />
        {/* Ad spend over the new customers it bought. "Est." because both
            halves are provisional: the platforms report today's spend with
            their own lag, and a sale nobody could attribute still divides the
            whole spend. Compared against whole days for the same reason — ad
            spend has no hourly grain to slice. */}
        <MetricTile
          label="Est. blended CAC"
          value={cac?.spend_reported ? cac.today : null}
          format={(v) => currency(v, true)}
          delta={cacDelta}
          invert
          comparison={
            !cac ? ''
              : !cac.spend_reported ? 'No ad spend reported'
                : cac.today == null ? `${currency(cac.today_spend)} spent, no new customers yet`
                  : `${currency(cac.baseline, true)} over ${cac.days} days · ${count(cac.today_new_orders)} new today`
          }
          tone="text-amber-600"
          loading={loadingOverview}
          href="/reports/daily"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Per-brand table. Always rendered for an aggregate view; for a single
            clinic there is only one row, so it would say nothing. */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {isAggregate ? 'Today by brand' : 'Today so far'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingOverview ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : isAggregate ? (
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40">
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Brand</th>
                    <th className="px-4 py-2 text-right font-medium">Revenue</th>
                    <th className="px-4 py-2 text-right font-medium">Orders</th>
                    <th className="px-4 py-2 text-right font-medium">vs 7-day avg</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {overview.brands.map(brand => {
                    // Withheld on a brand whose baseline is too thin, the same
                    // rule the tiles use, rather than printing a number that
                    // only says when two customers happened to buy.
                    const delta = brand.baseline_sufficient
                      ? percentChange(brand.revenue, brand.baseline_revenue)
                      : null
                    return (
                      <tr key={brand.clinicId}>
                        <td className="px-4 py-2.5">
                          <span className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: brand.color }} />
                            <span className="font-medium text-foreground">{brand.displayName}</span>
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{currency(brand.revenue)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{count(brand.orders)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {delta === null
                            ? <span className="text-muted-foreground">—</span>
                            : <span className={delta >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                {delta >= 0 ? '+' : ''}{delta.toFixed(0)}%
                              </span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-3">
                {[
                  { label: 'New orders', value: count(today?.new_orders) },
                  { label: 'Repeat orders', value: count((today?.orders ?? 0) - (today?.new_orders ?? 0)) },
                  { label: 'Revenue', value: currency(today?.revenue) },
                  { label: '7-day avg, same time', value: currency(baseline?.revenue) },
                  { label: 'Same time yesterday', value: currency(prior?.revenue) },
                  { label: 'Orders, 7-day avg', value: count(baseline?.orders) },
                ].map(cell => (
                  <div key={cell.label} className="bg-card p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {cell.label}
                    </div>
                    <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">{cell.value}</div>
                  </div>
                ))}
              </div>
            )}

            {overview?.baseline_unavailable?.length > 0 && (
              <p className="border-t px-4 py-3 text-xs text-muted-foreground">
                No seven-day baseline for {overview.baseline_unavailable.map(b => b.clinicId).join(', ')}, so
                they are left out of the comparison on both sides — their revenue is still in the totals.
              </p>
            )}

            {overview?.unavailable?.length > 0 && (
              <p className="border-t px-4 py-3 text-xs text-amber-700">
                Could not reach {overview.unavailable.join(', ')} — those brands are missing from the totals above.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Needs attention</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {loadingAttention ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : allItems.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <p className="text-sm text-muted-foreground">Nothing waiting on you.</p>
              </div>
            ) : (
              allItems.map(item => <AttentionRow key={item.id} item={item} />)
            )}
          </CardContent>
        </Card>
      </div>

      {/* The last seven days of the Daily Report, same table as /reports/daily.
          Full width because it is thirty columns wide; it scrolls sideways
          inside the card rather than widening the page. The columns come from
          the shared component, so a column added there appears here with no
          change on this page — which is the point of sharing it. */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 pb-3">
          <div>
            <CardTitle className="text-base">Last 7 days</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Today is still filling in. A channel's CVR is that channel's sales over
              its own leads in the same period, not a cohort — see the full report.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/reports/daily">Full report</Link>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {!currentClinicId ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">
              Select a brand to see its daily numbers — AOV and CAC are ratios, so
              there is no honest way to sum them across brands.
            </p>
          ) : !week && !loadingWeek ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">
              Daily numbers are unavailable for this brand right now.
            </p>
          ) : (
            <DailyReportTable
              rows={week ? [...week.rows].reverse() : PLACEHOLDER_WEEK}
              totals={week ? week.totals : {}}
              granularity="daily"
              currentPeriodKey={week?.current_period_key}
              clinic={week?.clinic || currentClinicId}
              loading={!week}
              pendingKeys={week?.pending_keys || []}
            />
          )}
        </CardContent>
      </Card>

      {/* Today's pace by the hour against the last 7 days — the Velocity page's
          chart. Science.co only, as the page is (backend services/velocity). */}
      {currentClinicId === 'scienceco' && <VelocityHourlyCard />}
    </div>
  )
}
